import { useCallback } from 'react';

import { Analytics } from '@trello/atlassian-analytics';

import type { PanelNames } from './Panels';
import { useSplitScreenSharedState } from './useSplitScreenSharedState';

interface UseSplitScreenResizeReturn {
  collapsePanel: (panelName: PanelNames) => void;
  getAnalyticsAttributes: (
    panelName: PanelNames,
    newWidth: number,
    isToggled: boolean,
  ) => Record<string, unknown>;
  isRightmostPanel: (panelName: PanelNames) => boolean;
  getMaxWidth: (panelName: PanelNames) => number;
  getMinWidth: (panelName: PanelNames) => number;
  sendResizeAnalyticsEvent: (panelName: PanelNames, newWidth: number) => void;
}

export const useSplitScreenResize = (): UseSplitScreenResizeReturn => {
  const {
    updateResizedPanelWidth,
    plannerPosition,
    areMultiplePanelsOpen,
    panels,
    togglePanel,
  } = useSplitScreenSharedState();

  const isRightmostPanel = useCallback(
    (panelName: PanelNames) => {
      if (panelName === 'inbox') return false;

      if (panelName === 'planner')
        return plannerPosition === 'rightmost' || !areMultiplePanelsOpen;

      if (panelName === 'board') return true;

      return false;
    },
    [plannerPosition, areMultiplePanelsOpen],
  );

  const getAnalyticsAttributes = useCallback(
    (panelName: PanelNames, newWidth: number, isToggled: boolean) => {
      if (isToggled) {
        return {
          isInboxOpen: panelName !== 'inbox' && panels.inbox,
          isPlannerOpen: panelName !== 'planner' && panels.planner,
          isBoardOpen: panelName !== 'board' && panels.board,
        };
      }

      return {
        isInboxOpen: panels.inbox,
        isPlannerOpen: panels.planner,
        isBoardOpen: panels.board,
        newPanelSize: newWidth,
      };
    },
    [panels],
  );

  const sendResizeAnalyticsEvent = useCallback(
    (panelName: PanelNames, newWidth: number) => {
      Analytics.sendUIEvent({
        action: 'resized',
        actionSubject: 'panel',
        actionSubjectId: `${panelName}Panel`,
        source: 'personalProductivitySplitScreenResizingDivider',
        attributes: getAnalyticsAttributes(panelName, newWidth, false),
      });
    },
    [getAnalyticsAttributes],
  );

  const collapsePanel = useCallback(
    (panelName: PanelNames) => {
      const previousPanelWidth = panels.panelWidths[panelName].width;
      togglePanel(panelName);
      updateResizedPanelWidth(
        panelName,
        previousPanelWidth === 0
          ? panels.panelWidths[panelName].minWidth
          : previousPanelWidth,
      );

      Analytics.sendUIEvent({
        action: 'collapsed',
        actionSubject: 'panel',
        actionSubjectId: `${panelName}Panel`,
        source: 'personalProductivitySplitScreenResizingDivider',
        attributes: getAnalyticsAttributes(panelName, 0, true),
      });
    },
    [
      panels.panelWidths,
      togglePanel,
      updateResizedPanelWidth,
      getAnalyticsAttributes,
    ],
  );

  const getMaxWidth = useCallback(
    (panelName: PanelNames) => {
      return panels.panelWidths[panelName].maxWidth;
    },
    [panels.panelWidths],
  );

  const getMinWidth = useCallback(
    (panelName: PanelNames) => {
      return panels.panelWidths[panelName].minWidth;
    },
    [panels.panelWidths],
  );

  return {
    collapsePanel,
    getMaxWidth,
    getMinWidth,
    getAnalyticsAttributes,
    isRightmostPanel,
    sendResizeAnalyticsEvent,
  };
};
